import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { StudySession } from '../../../services/api';
import { Card, SpaceCard } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Calendar, CheckCircle, ChevronRight, Clock } from 'lucide-react';
import { FlowStep } from '../NewStudySession';
import { MixedText } from '../../../utils/rtl';

interface CompleteStepProps {
  session: StudySession;
  courseSessions: StudySession[];
  onSessionUpdate: (session: StudySession) => void;
  onStepChange: (step: FlowStep) => void;
  isRTL: boolean;
}

const CompleteStep: React.FC<CompleteStepProps> = ({
  session,
  courseSessions,
  onSessionUpdate,
  onStepChange,
  isRTL
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Helper functions to analyze course sessions
  const getSortedSessions = () => {
    // Sort sessions by creation date (oldest first)
    return [...courseSessions].sort((a, b) => 
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
  };

  const getCurrentSessionIndex = () => {
    const sortedSessions = getSortedSessions();
    return sortedSessions.findIndex(s => s.id === session.id);
  };

  const getNextSession = () => {
    const sortedSessions = getSortedSessions();
    const currentIndex = getCurrentSessionIndex();
    if (currentIndex >= 0 && currentIndex < sortedSessions.length - 1) {
      return sortedSessions[currentIndex + 1];
    }
    return null;
  };

  const getPreviousCompletedSessions = () => {
    const sortedSessions = getSortedSessions();
    const currentIndex = getCurrentSessionIndex();
    return sortedSessions
      .slice(0, currentIndex)
      .filter(s => s.completed_at)
      .reverse() // Show most recent first
      .slice(0, 3); // Show only last 3
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(isRTL ? 'he-IL' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const nextSession = getNextSession();
  const previousSessions = getPreviousCompletedSessions();
  
  // Don't show navigation sections if there's only one session
  const showNavigationSections = courseSessions.length > 1;

  return (
    <div className="space-y-8">
      {/* Celebration Header */}
      <div className="text-center relative">
        {/* Animated background particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className={`absolute w-2 h-2 bg-gradient-to-r ${
                i % 3 === 0 ? 'from-yellow-400 to-yellow-500' :
                i % 3 === 1 ? 'from-vibrant-orange to-vibrant-orange/90' :
                'from-green-400 to-green-500'
              } rounded-full animate-ping`}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${i * 0.1}s`,
                animationDuration: `${2 + Math.random() * 2}s`
              }}
            />
          ))}
        </div>
        <div className="relative z-10">
          <div className="w-24 h-24 bg-gradient-to-r from-vibrant-orange to-vibrant-orange/90 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl">
            <img 
              src="/astronaut_winning.png" 
              alt="StudyBel Astronaut Winning" 
              className="w-16 h-16 filter brightness-110"
            />
          </div>
          <h2 className="text-4xl font-bold text-bg-secondary mb-4">
            <MixedText>{t('sessions:complete_step.title', 'Session Complete!')}</MixedText>
          </h2>
          <p className="text-bg-secondary text-xl font-semibold">
            <MixedText>{session.title}</MixedText>
          </p>
        </div>
      </div>

      {/* Back to Course Button */}
      <div className="flex justify-center">
        <Button
          onClick={() => navigate(`/course/${session.course_id}`)}
          variant="secondary"
          size="lg"
          className="font-hebrew"
        >
          <MixedText>{t('sessions:complete_step.course_overview.back_to_course', 'Back to Course')}</MixedText>
        </Button>
      </div>

      {/* Next Steps Grid - Only show if there are multiple sessions */}
      {showNavigationSections && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Next Session */}
          <SpaceCard className="hover:shadow-xl transition-all duration-300 hover:scale-[1.02]">
            <div className="p-8">
              <h3 className={`text-xl font-semibold text-surface-primary mb-6 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                <ChevronRight className={`w-6 h-6 text-vibrant-orange ${isRTL ? 'rotate-180 ml-3' : 'mr-3'}`} />
                <MixedText>{t('sessions:complete_step.next_session.title', 'Next Session')}</MixedText>
              </h3>
              
              {nextSession ? (
                <div className="space-y-4">
                  <div className="p-4 bg-gradient-to-br from-vibrant-orange/5 to-vibrant-orange/10 rounded-xl border border-vibrant-orange/20">
                    <h4 className={`font-semibold text-surface-primary mb-2 line-clamp-2 ${isRTL ? 'text-right' : ''}`}>
                      <MixedText>{nextSession.title}</MixedText>
                    </h4>
                    {nextSession.ai_generated_description && (
                      <p className={`text-surface-secondary text-sm line-clamp-3 mb-3 ${isRTL ? 'text-right' : ''}`}>
                        <MixedText>{nextSession.ai_generated_description}</MixedText>
                      </p>
                    )}
                    <div className={`flex items-center text-sm text-surface-muted mb-4 ${isRTL ? 'flex-row-reverse justify-start' : ''}`}>
                      <Calendar className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      <MixedText>{formatDate(nextSession.created_at)}</MixedText>
                    </div>
                  </div>
                  <p className={`text-surface-secondary text-center mb-4 ${isRTL ? 'text-right' : ''}`}>
                    <MixedText>{t('sessions:complete_step.next_session.description', 'Keep your momentum going')}</MixedText>
                  </p>
                  <Button
                    onClick={() => navigate(`/study-session/${nextSession.id}`)}
                    className="w-full bg-gradient-to-r from-vibrant-orange to-vibrant-orange/90 hover:from-vibrant-orange/90 hover:to-vibrant-orange text-astronaut-white font-hebrew"
                  >
                    <MixedText>{t('sessions:complete_step.next_session.start_next', 'Continue Learning')}</MixedText>
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-astronaut-white" />
                  </div>
                  <p className="text-surface-secondary">
                    <MixedText>{t('sessions:complete_step.next_session.no_next', 'This is your latest session')}</MixedText>
                  </p>
                </div>
              )}
            </div>
          </SpaceCard>

          {/* Previous Sessions */}
          <SpaceCard className="hover:shadow-xl transition-all duration-300 hover:scale-[1.02]">
            <div className="p-8">
              <h3 className={`text-xl font-semibold text-surface-primary mb-6 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Clock className={`w-6 h-6 text-muted-purple ${isRTL ? 'ml-3' : 'mr-3'}`} />
                <MixedText>{t('sessions:complete_step.previous_sessions.title', 'Previous Sessions')}</MixedText>
              </h3>
              
              {previousSessions.length > 0 ? (
                <div className="space-y-3">
                  {previousSessions.map((prevSession) => (
                    <div
                      key={prevSession.id}
                      className="p-4 bg-gradient-to-br from-muted-purple/5 to-muted-purple/10 rounded-xl border border-muted-purple/20 hover:border-muted-purple/30 transition-all duration-200 cursor-pointer group"
                      onClick={() => navigate(`/study-session/${prevSession.id}`)}
                    >
                      <div className={`flex items-start justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className="flex-1 min-w-0">
                          <h4 className={`font-medium text-surface-primary group-hover:text-muted-purple transition-colors line-clamp-1 mb-1 ${isRTL ? 'text-right' : ''}`}>
                            <MixedText>{prevSession.title}</MixedText>
                          </h4>
                          <div className={`flex items-center text-sm text-surface-secondary ${isRTL ? 'flex-row-reverse justify-start' : ''}`}>
                            <CheckCircle className={`w-3 h-3 text-green-500 flex-shrink-0 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span className={`${isRTL ? 'ml-2' : 'mr-2'}`}>
                              <MixedText>{t('sessions:complete_step.previous_sessions.completed_on', 'Completed on')}</MixedText>
                            </span>
                            <MixedText>{formatDate(prevSession.completed_at!)}</MixedText>
                          </div>
                        </div>
                        <ChevronRight className={`w-4 h-4 text-surface-secondary group-hover:text-muted-purple transition-colors flex-shrink-0 mt-1 ${isRTL ? 'rotate-180' : ''}`} />
                      </div>
                    </div>
                  ))}
                  
                  {courseSessions.filter(s => s.completed_at).length > 3 && (
                    <Button
                      onClick={() => navigate(`/course/${session.course_id}`)}
                      variant="ghost"
                      size="sm"
                      className="w-full mt-3 text-muted-purple hover:text-muted-purple hover:bg-muted-purple/10 font-hebrew"
                    >
                      <MixedText>{t('sessions:complete_step.previous_sessions.view_all', 'View All Sessions')}</MixedText>
                    </Button>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-muted-purple to-muted-purple/80 rounded-full flex items-center justify-center mx-auto mb-4">
                    <img 
                      src="/astronaut_idea.png" 
                      alt="StudyBel Astronaut Idea" 
                      className="w-10 h-10 filter brightness-110"
                    />
                  </div>
                  <p className="text-surface-secondary">
                    <MixedText>{t('sessions:complete_step.previous_sessions.no_previous', 'This is your first session')}</MixedText>
                  </p>
                </div>
              )}
            </div>
          </SpaceCard>
        </div>
      )}
    </div>
  );
};

export default CompleteStep; 